import { Suspense, lazy, type ComponentType } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ProtectedRoute } from '@features/auth/components/ProtectedRoute';
import { DashboardLayout } from '@shared/components/Layout/DashboardLayout';
import { PageFallback } from '@shared/components/PageFallback';
import { I18nNamespaces } from '@/i18n/namespaces';
import { MigrationPlaceholder } from '@shared/components/MigrationPlaceholder';
import { MIGRATION_PLACEHOLDER_ROUTES } from '@shared/config/migrationPlaceholderRoutes';
import { transportRouteDefinitions } from '@features/transport/routeDefinitions';
import { structuralSchemeConfig } from '@/config/structuralSchemeConfig';

function lazyPage<T extends Record<string, ComponentType>, K extends keyof T>(
  factory: () => Promise<T>,
  exportName: K,
) {
  return lazy(() =>
    factory().then((module) => ({
      default: module[exportName] as ComponentType,
    })),
  );
}

const LoginPage = lazyPage(() => import('@features/auth/pages/LoginPage'), 'LoginPage');
const DashboardPage = lazyPage(() => import('@features/dashboard/pages/DashboardPage'), 'DashboardPage');

// Customers
const CustomersPage = lazyPage(() => import('@features/customers/pages/CustomersPage'), 'CustomersPage');
const CustomerDetailPage = lazyPage(
  () => import('@features/customers/pages/CustomerDetailPage'),
  'CustomerDetailPage',
);
const CustomersDebtorsPage = lazyPage(
  () => import('@features/customers/pages/CustomersDebtorsPage'),
  'CustomersDebtorsPage',
);
const CustomersActivityPage = lazyPage(
  () => import('@features/customers/pages/CustomersActivityPage'),
  'CustomersActivityPage',
);
const IpNetworkPage = lazyPage(() => import('@features/ip-network/pages/IpNetworkPage'), 'IpNetworkPage');
const CustomerGrowthChartPage = lazyPage(
  () => import('@features/customers/pages/CustomerGrowthChartPage'),
  'CustomerGrowthChartPage'
);
const CustomerActivityChartPage = lazyPage(
  () => import('@features/customers/pages/CustomerActivityChartPage'),
  'CustomerActivityChartPage'
);

const CustomersTagsPage = lazyPage(
  () => import('@features/customers/pages/CustomersTagsPage'),
  'CustomersTagsPage',
);
const CustomersUnknownMacPage = lazyPage(
  () => import('@features/customers/pages/CustomersUnknownMacPage'),
  'CustomersUnknownMacPage',
);
const CustomersSmsListPage = lazyPage(
  () => import('@features/customers/pages/CustomersSmsListPage'),
  'CustomersSmsListPage',
);

// Messages
const CallListPage = lazyPage(() => import('@features/messages/pages/CallListPage'), 'CallListPage');
const InboxPage = lazyPage(() => import('@features/messages/pages/InboxPage'), 'InboxPage');
const OutboxPage = lazyPage(() => import('@features/messages/pages/OutboxPage'), 'OutboxPage');
const ArchivePage = lazyPage(() => import('@features/messages/pages/ArchivePage'), 'ArchivePage');

// Support
const SupportPage = lazyPage(() => import('@features/support/pages/SupportPage'), 'SupportPage');

// Tasks
const TasksPage = lazyPage(() => import('@features/tasks/pages/TasksPage'), 'TasksPage');
const TaskDetailPage = lazyPage(() => import('@features/tasks/pages/TaskDetailPage'), 'TaskDetailPage');
const MobileTaskListPage = lazyPage(
  () => import('@features/tasks/pages/MobileTaskListPage'),
  'MobileTaskListPage',
);
const SLADashboardPage = lazyPage(
  () => import('@features/tasks/pages/SLADashboardPage'),
  'SLADashboardPage',
);
const QualityDashboardPage = lazyPage(
  () => import('@features/tasks/pages/QualityDashboardPage'),
  'QualityDashboardPage',
);
const WebhooksPage = lazyPage(() => import('@features/tasks/pages/WebhooksPage'), 'WebhooksPage');
const TaskSchedulesPage = lazyPage(
  () => import('@features/tasks/pages/TaskSchedulesPage'),
  'TaskSchedulesPage',
);

// Accounting
const AccountingBalanceSummaryPage = lazyPage(
  () => import('@features/accounting/pages/AccountingBalanceSummaryPage'),
  'AccountingBalanceSummaryPage',
);
const AccountingBalance0_3Page = lazyPage(
  () => import('@features/accounting/pages/AccountingBalance0_3Page'),
  'AccountingBalance0_3Page',
);
const AccountingSuspectPage = lazyPage(
  () => import('@features/accounting/pages/AccountingSuspectPage'),
  'AccountingSuspectPage',
);
const AccountingHopelessPage = lazyPage(
  () => import('@features/accounting/pages/AccountingHopelessPage'),
  'AccountingHopelessPage',
);
const AccountingChangesPage = lazyPage(
  () => import('@features/accounting/pages/AccountingChangesPage'),
  'AccountingChangesPage',
);
const AccountingFeePage = lazyPage(
  () => import('@features/accounting/pages/AccountingFeePage'),
  'AccountingFeePage',
);
const AccountingFeePerBuildingPage = lazyPage(
  () => import('@features/accounting/pages/AccountingFeePerBuildingPage'),
  'AccountingFeePerBuildingPage',
);

// Nodes
const NodesListPage = lazyPage(() => import('@features/nodes/pages/NodesListPage'), 'NodesListPage');
const NodeDetailPage = lazyPage(() => import('@features/nodes/pages/NodeDetailPage'), 'NodeDetailPage');

// Cable
const CableDuctPage = lazyPage(() => import('@features/cable/pages/CableDuctPage'), 'CableDuctPage');
const CableCrossesPage = lazyPage(
  () => import('@features/cable/pages/CableCrossesPage'),
  'CableCrossesPage',
);
const CrossDetailPage = lazyPage(
  () => import('@features/cable/pages/CrossDetailPage'),
  'CrossDetailPage',
);
const CrossCommutationTablePage = lazyPage(
  () => import('@features/cable/pages/CrossCommutationTablePage'),
  'CrossCommutationTablePage',
);
const CableLinesPage = lazyPage(() => import('@features/cable/pages/CableLinesPage'), 'CableLinesPage');
const CableLineDetailPage = lazyPage(
  () => import('@features/cable/pages/CableLineDetailPage'),
  'CableLineDetailPage',
);
const CableSplittersPage = lazyPage(
  () => import('@features/cable/pages/CableSplittersPage'),
  'CableSplittersPage',
);
const CableSplitterDetailPage = lazyPage(
  () => import('@features/cable/pages/CableSplitterDetailPage'),
  'CableSplitterDetailPage',
);
const CableGlobalSchemePage = lazyPage(
  () => import('@features/cable/pages/CableGlobalSchemePage'),
  'CableGlobalSchemePage',
);
const CommutationSchemePage = lazyPage(
  () => import('@features/cable/pages/CommutationSchemePage'),
  'CommutationSchemePage',
);
const StructuralSchemePage = lazyPage(
  () => import('@features/cable/structural/StructuralSchemePage'),
  'StructuralSchemePage',
);
const CableTrunksPage = lazyPage(() => import('@features/cable/pages/CableTrunksPage'), 'CableTrunksPage');
const TransportSchedulesPage = lazyPage(
  () => import('@features/transportSchedule/pages/TransportSchedulesPage'),
  'TransportSchedulesPage'
);
const TransportScheduleFormPage = lazyPage(
  () => import('@features/transportSchedule/pages/TransportScheduleFormPage'),
  'TransportScheduleFormPage'
);
const TransportScheduleDetailPage = lazyPage(
  () => import('@features/transportSchedule/pages/TransportScheduleDetailPage'),
  'TransportScheduleDetailPage'
);
const CableTrunkDetailPage = lazyPage(() => import('@features/cable/pages/CableTrunkDetailPage'), 'CableTrunkDetailPage');

// Devices
const DevicesPage = lazyPage(() => import('@features/devices/pages/DevicesPage'), 'DevicesPage');
const DeviceDetailPage = lazyPage(
  () => import('@features/devices/pages/DeviceDetailPage'),
  'DeviceDetailPage',
);
const DeviceNewPage = lazyPage(() => import('@features/devices/pages/DeviceNewPage'), 'DeviceNewPage');
const DeviceEditPage = lazyPage(() => import('@features/devices/pages/DeviceEditPage'), 'DeviceEditPage');
const MACSearchPage = lazyPage(() => import('@features/devices/pages/MACSearchPage'), 'MACSearchPage');
// Phase 3: SNMP Monitoring
const SNMPMonitoringPage = lazy(
  () => import('@features/devices/pages/SNMPMonitoringPage').then(m => ({ default: m.SNMPMonitoringPage })),
);
const DevicesSwitchesPage = lazyPage(
  () => import('@features/devices/pages/DevicesSwitchesPage'),
  'DevicesSwitchesPage',
);
const DevicesOLTPage = lazyPage(() => import('@features/devices/pages/DevicesOLTPage'), 'DevicesOLTPage');
const DevicesONUPage = lazyPage(() => import('@features/devices/pages/DevicesONUPage'), 'DevicesONUPage');
const DevicesRadioPage = lazyPage(
  () => import('@features/devices/pages/DevicesRadioPage'),
  'DevicesRadioPage',
);
const DevicesMedicalPage = lazyPage(
  () => import('@features/devices/pages/DevicesMedicalPage'),
  'DevicesMedicalPage',
);
const DevicesSystemPage = lazyPage(
  () => import('@features/devices/pages/DevicesSystemPage'),
  'DevicesSystemPage',
);
const DevicesCustomSectionPage = lazyPage(
  () => import('@features/devices/pages/DevicesCustomSectionPage'),
  'DevicesCustomSectionPage',
);
const DevicesCustomSharedPage = lazyPage(
  () => import('@features/devices/pages/DevicesCustomSectionPage'),
  'DevicesCustomSharedPage',
);
const DevicesCWDMPage = lazyPage(
  () => import('@features/devices/pages/DevicesCWDMPage'),
  'DevicesCWDMPage',
);
const CwdmDetailPage = lazyPage(
  () => import('@features/devices/pages/CwdmDetailPage'),
  'CwdmDetailPage',
);
const DevicesVLANPage = lazyPage(
  () => import('@features/devices/pages/DevicesVLANPage'),
  'DevicesVLANPage',
);
const IfaceErrorsParkPage = lazyPage(
  () => import('@features/devices/pages/IfaceErrorsParkPage'),
  'IfaceErrorsParkPage',
);

const IfaceErrorLogPage = lazyPage(
  () => import('@features/devices/pages/IfaceErrorLogPage'),
  'IfaceErrorLogPage',
);
const DoubleCustomerPage = lazyPage(
  () => import('@features/devices/pages/DoubleCustomerPage'),
  'DoubleCustomerPage',
);
const ONUUnplugPage = lazyPage(
  () => import('@features/devices/pages/ONUUnplugPage'),
  'ONUUnplugPage',
);
const OLTONUPage = lazyPage(
  () => import('@features/devices/pages/OLTONUPage'),
  'OLTONUPage',
);
const PortNewMACPage = lazyPage(
  () => import('@features/devices/pages/PortNewMACPage'),
  'PortNewMACPage',
);
const RingFindPage = lazyPage(
  () => import('@features/devices/pages/RingFindPage'),
  'RingFindPage',
);
const ActivityLogFleetPage = lazyPage(
  () => import('@features/devices/pages/ActivityLogFleetPage'),
  'ActivityLogFleetPage',
);
const RadioBadLevelPage = lazyPage(
  () => import('@features/devices/pages/RadioBadLevelPage'),
  'RadioBadLevelPage',
);
const FirmwareReportPage = lazyPage(
  () => import('@features/devices/pages/FirmwareReportPage'),
  'FirmwareReportPage',
);
const ElectroReportPage = lazyPage(
  () => import('@features/devices/pages/ElectroReportPage'),
  'ElectroReportPage',
);
const PelengPage = lazyPage(
  () => import('@features/devices/pages/PelengPage'),
  'PelengPage',
);
const DevicesVLANDetailPage = lazyPage(
  () => import('@features/devices/pages/DevicesVLANDetailPage'),
  'DevicesVLANDetailPage',
);
const ONUDetailPage = lazyPage(
  () => import('@features/devices/pages/ONUDetailPage'),
  'ONUDetailPage',
);
const VendorProfilesPage = lazyPage(
  () => import('@features/devices/pages/VendorProfilesPage'),
  'VendorProfilesPage',
);

// Equipment Catalog
const EquipmentModelsPage = lazyPage(
  () => import('@features/equipment/pages/EquipmentModelsPage'),
  'EquipmentModelsPage',
);
const EquipmentWebhooksPage = lazyPage(
  () => import('@features/equipment/pages/WebhooksPage'),
  'WebhooksPage',
);
const UnknownFingerprintsPage = lazyPage(
  () => import('@features/equipment/pages/UnknownFingerprintsPage'),
  'UnknownFingerprintsPage',
);

// Warehouse
const InventoryListPage = lazyPage(
  () => import('@features/inventory/pages/InventoryListPage'),
  'InventoryListPage',
);
const InventoryCatalogPage = lazyPage(
  () => import('@features/inventory/pages/InventoryCatalogPage'),
  'InventoryCatalogPage',
);
const InventoryPrintJobsPage = lazyPage(
  () => import('@features/inventory/pages/InventoryPrintJobsPage'),
  'InventoryPrintJobsPage',
);
const InventoryOperationPage = lazyPage(
  () => import('@features/inventory/pages/InventoryOperationPage'),
  'InventoryOperationPage',
);
const InventoryDetailPage = lazyPage(
  () => import('@features/inventory/pages/InventoryDetailPage'),
  'InventoryDetailPage',
);
const InventoryReportsPage = lazyPage(
  () => import('@features/inventory/pages/InventoryReportsPage'),
  'InventoryReportsPage',
);
const WarehouseSettingsPage = lazyPage(
  () => import('@features/inventory/pages/WarehouseSettingsPage'),
  'WarehouseSettingsPage',
);
const WarehouseSuppliersPage = lazyPage(
  () => import('@features/warehouse/pages/WarehouseSuppliersPage'),
  'WarehouseSuppliersPage',
);

// Map
const MapBuildingsPage = lazyPage(
  () => import('@features/map/pages/MapBuildingsPage'),
  'MapBuildingsPage',
);
const MapOwnersPage = lazyPage(() => import('@features/map/pages/MapOwnersPage'), 'MapOwnersPage');
const MapTenantsPage = lazyPage(() => import('@features/map/pages/MapTenantsPage'), 'MapTenantsPage');
const MapKeysPage = lazyPage(() => import('@features/map/pages/MapKeysPage'), 'MapKeysPage');
const MapObjectsPage = lazyPage(() => import('@features/map/pages/MapObjectsPage'), 'MapObjectsPage');

// Employees
const EmployeesMotivationPage = lazyPage(
  () => import('@features/employees/pages/EmployeesMotivationPage'),
  'EmployeesMotivationPage',
);
const EmployeesVacationPage = lazyPage(
  () => import('@features/employees/pages/EmployeesVacationPage'),
  'EmployeesVacationPage',
);
const EmployeesReportsPage = lazyPage(
  () => import('@features/employees/pages/EmployeesReportsPage'),
  'EmployeesReportsPage',
);
const EmployeesDivisionsPage = lazyPage(
  () => import('@features/employees/pages/EmployeesDivisionsPage'),
  'EmployeesDivisionsPage',
);

// Transport
const transportRoutes = [
  {
    ...transportRouteDefinitions[0],
    Page: lazyPage(transportRouteDefinitions[0].load, transportRouteDefinitions[0].exportName),
  },
  {
    ...transportRouteDefinitions[1],
    Page: lazyPage(transportRouteDefinitions[1].load, transportRouteDefinitions[1].exportName),
  },
];

// Users
const UsersMessagesPage = lazyPage(
  () => import('@features/users/pages/UsersMessagesPage'),
  'UsersMessagesPage',
);
const UsersMainPage = lazyPage(() => import('@features/users/pages/UsersMainPage'), 'UsersMainPage');
const UsersDetailPage = lazyPage(
  () => import('@features/users/pages/UsersDetailPage'),
  'UsersDetailPage',
);
const UsersEditPage = lazyPage(
  () => import('@features/users/pages/UsersEditPage'),
  'UsersEditPage',
);
const UsersProfilesPage = lazyPage(
  () => import('@features/users/pages/UsersProfilesPage'),
  'UsersProfilesPage',
);
const ProfileCreatePage = lazyPage(
  () => import('@features/users/pages/ProfileCreatePage'),
  'ProfileCreatePage',
);
const ProfileEditPage = lazyPage(
  () => import('@features/users/pages/ProfileEditPage'),
  'ProfileEditPage',
);
const UsersHistoryPage = lazyPage(
  () => import('@features/users/pages/UsersHistoryPage'),
  'UsersHistoryPage',
);

// Settings
const SettingsMainPage = lazyPage(
  () => import('@features/settings/pages/SettingsMainPage'),
  'SettingsMainPage',
);
const SettingsAdditionalServicesPage = lazyPage(
  () => import('@features/settings/pages/SettingsAdditionalServicesPage'),
  'SettingsAdditionalServicesPage',
);
const TariffsPage = lazyPage(() => import('@features/tariffs/pages/TariffsPage'), 'TariffsPage');
const TariffTrafficPage = lazyPage(
  () => import('@features/tariffs/pages/TariffTrafficPage'),
  'TariffTrafficPage',
);
const TariffGroupsPage = lazyPage(
  () => import('@features/tariffs/pages/TariffGroupsPage'),
  'TariffGroupsPage',
);
const TariffGroupDetailPage = lazyPage(
  () => import('@features/tariffs/pages/TariffGroupDetailPage'),
  'TariffGroupDetailPage',
);
const ModuleSchedulesPage = lazyPage(
  () => import('@features/modules/pages/ModuleSchedulesPage'),
  'ModuleSchedulesPage',
);
const SettingsModulesPage = lazyPage(
  () => import('@features/settings/pages/SettingsModulesPage'),
  'SettingsModulesPage',
);
const SettingsPluginsPage = lazyPage(
  () => import('@features/settings/pages/SettingsPluginsPage'),
  'SettingsPluginsPage',
);
const SettingsBillingModulesPage = lazyPage(
  () => import('@features/settings/pages/SettingsBillingModulesPage'),
  'SettingsBillingModulesPage',
);
const ModuleDetailPage = lazyPage(
  () => import('@features/settings/pages/ModuleDetailPage'),
  'ModuleDetailPage',
);
const SettingsSupportInterfacePage = lazyPage(
  () => import('@features/settings/pages/SettingsSupportInterfacePage'),
  'SettingsSupportInterfacePage',
);
const SettingsNodeTypesPage = lazyPage(
  () => import('@features/settings/pages/SettingsNodeTypesPage'),
  'SettingsNodeTypesPage',
);
const SettingsNodeIconsPage = lazyPage(
  () => import('@features/settings/pages/SettingsNodeIconsPage'),
  'SettingsNodeIconsPage',
);
const SettingsAsteriskPage = lazyPage(
  () => import('@features/settings/pages/SettingsAsteriskPage'),
  'SettingsAsteriskPage',
);
const SettingsObserverPage = lazyPage(
  () => import('@features/settings/pages/SettingsObserverPage'),
  'SettingsObserverPage',
);
const SettingsPollerPage = lazyPage(
  () => import('@features/settings/pages/SettingsPollerPage'),
  'SettingsPollerPage',
);
const SettingsLicensePage = lazyPage(
  () => import('@features/settings/pages/SettingsLicensePage'),
  'SettingsLicensePage',
);
const SettingsLdapPage = lazyPage(
  () => import('@features/settings/pages/SettingsLdapPage'),
  'default',
);
const SettingsMapPage = lazyPage(
  () => import('@features/settings/pages/SettingsMapPage'),
  'SettingsMapPage',
);
const SettingsAddressPage = lazyPage(
  () => import('@features/settings/pages/SettingsAddressPage'),
  'SettingsAddressPage',
);
const SettingsCableLinesPage = lazyPage(
  () => import('@features/settings/pages/SettingsCableLinesPage'),
  'SettingsCableLinesPage',
);
const SettingsCustomersPage = lazyPage(
  () => import('@features/settings/pages/SettingsCustomersPage'),
  'SettingsCustomersPage',
);
const SettingsTasksPage = lazyPage(
  () => import('@features/settings/pages/SettingsTasksPage'),
  'SettingsTasksPage',
);
const SettingsBasicPage = lazyPage(
  () => import('@features/settings/pages/SettingsBasicPage'),
  'SettingsBasicPage',
);
const SettingsEquipmentPage = lazyPage(
  () => import('@features/settings/pages/SettingsEquipmentPage'),
  'SettingsEquipmentPage',
);
const SettingsDeviceSnmpProfilesPage = lazyPage(
  () => import('@features/settings/pages/SettingsDeviceSnmpProfilesPage'),
  'SettingsDeviceSnmpProfilesPage',
);
const SettingsDeviceSnmpProfileEditPage = lazyPage(
  () => import('@features/settings/pages/SettingsDeviceSnmpProfilesPage'),
  'SettingsDeviceSnmpProfileEditPage',
);
const SettingsEquipmentSatellitesPage = lazyPage(
  () => import('@features/settings/pages/SettingsEquipmentSatellitesPage'),
  'SettingsEquipmentSatellitesPage',
);
const SettingsEquipmentSatelliteEditPage = lazyPage(
  () => import('@features/settings/pages/SettingsEquipmentSatelliteEditPage'),
  'SettingsEquipmentSatelliteEditPage',
);
const SettingsThemePage = lazyPage(
  () => import('@features/settings/pages/SettingsThemePage'),
  'SettingsThemePage',
);
const SettingsGeocodingPage = lazyPage(
  () => import('@features/settings/pages/SettingsGeocodingPage'),
  'SettingsGeocodingPage',
);
const SettingsCableLinesPalettesPage = lazyPage(
  () => import('@features/settings/pages/SettingsCableLinesPalettesPage'),
  'SettingsCableLinesPalettesPage',
);
const SettingsCableLineTypeEditPage = lazyPage(
  () => import('@features/settings/pages/SettingsCableLineTypeEditPage'),
  'SettingsCableLineTypeEditPage',
);
const SettingsCableCatalogPage = lazyPage(
  () => import('@features/settings/pages/SettingsCableCatalogPage'),
  'SettingsCableCatalogPage',
);
const SettingsCableCatalogEditPage = lazyPage(
  () => import('@features/settings/pages/SettingsCableCatalogEditPage'),
  'SettingsCableCatalogEditPage',
);
const SettingsCableColorsPage = lazyPage(
  () => import('@features/settings/pages/SettingsCableColorsPage'),
  'SettingsCableColorsPage',
);
const SettingsCableLinesOtherPage = lazyPage(
  () => import('@features/settings/pages/SettingsCableLinesOtherPage'),
  'SettingsCableLinesOtherPage',
);
const SettingsAdditionalFieldsPage = lazyPage(
  () => import('@features/settings/pages/SettingsAdditionalFieldsPage'),
  'SettingsAdditionalFieldsPage',
);

const SettingsDocumentsPage = lazyPage(
  () => import('@features/settings/pages/SettingsDocumentsPage'),
  'SettingsDocumentsPage',
);

const SettingsObserverOidsPage = lazyPage(
  () => import('@features/settings/pages/SettingsObserverOidsPage'),
  'SettingsObserverOidsPage',
);

const SettingsObserverProfilesPage = lazyPage(
  () => import('@features/settings/pages/SettingsObserverProfilesPage'),
  'SettingsObserverProfilesPage',
);

const SettingsObserverDevicesPage = lazyPage(
  () => import('@features/settings/pages/SettingsObserverDevicesPage'),
  'SettingsObserverDevicesPage',
);

const SettingsObserverReadingsPage = lazyPage(
  () => import('@features/settings/pages/SettingsObserverReadingsPage'),
  'SettingsObserverReadingsPage',
);

const SettingsObserverLogPage = lazyPage(
  () => import('@features/settings/pages/SettingsObserverLogPage'),
  'SettingsObserverLogPage',
);

const SettingsAdditionalFieldEditPage = lazyPage(
  () => import('@features/settings/pages/SettingsAdditionalFieldEditPage'),
  'SettingsAdditionalFieldEditPage',
);

const SettingsObjectMarksPage = lazyPage(
  () => import('@features/settings/pages/SettingsObjectMarksPage'),
  'SettingsObjectMarksPage',
);

const SettingsObjectMarkEditPage = lazyPage(
  () => import('@features/settings/pages/SettingsObjectMarkEditPage'),
  'SettingsObjectMarkEditPage',
);

// Backup
const BackupCenterPage = lazyPage(
  () => import('@features/backup/pages/BackupCenterPage'),
  'BackupCenterPage',
);
// Advertisement, Notepad, Statistics, SMS
const AdvertisementListPage = lazyPage(
  () => import('@features/advertisement/pages/AdvertisementListPage'),
  'AdvertisementListPage',
);
const AdvertisementFormPage = lazyPage(
  () => import('@features/advertisement/pages/AdvertisementFormPage'),
  'AdvertisementFormPage',
);
const AdvertisementDetailPage = lazyPage(
  () => import('@features/advertisement/pages/AdvertisementDetailPage'),
  'AdvertisementDetailPage',
);
const NotepadPage = lazyPage(() => import('@features/notepad/pages/NotepadPage'), 'NotepadPage');
const StatisticsPage = lazyPage(() => import('@features/statistics/pages/StatisticsPage'), 'StatisticsPage');
const SmsSendPage = lazyPage(() => import('@features/sms/pages/SmsSendPage'), 'SmsSendPage');

// Monitoring
const ServicesMonitoringPage = lazyPage(
  () => import('@/pages/monitoring/ServicesMonitoringPage'),
  'ServicesMonitoringPage',
);
const RabbitMQStatusPage = lazyPage(
  () => import('@/pages/monitoring/RabbitMQStatusPage'),
  'RabbitMQStatusPage',
);
const RedisStatusPage = lazyPage(
  () => import('@/pages/monitoring/RedisStatusPage'),
  'RedisStatusPage',
);
const GrafanaPage = lazyPage(() => import('@/pages/monitoring/GrafanaPage'), 'GrafanaPage');
const PrometheusPage = lazyPage(() => import('@/pages/monitoring/PrometheusPage'), 'PrometheusPage');
const DatabasePerformancePage = lazy(
  () => import('@/pages/monitoring/DatabasePerformancePage').then(m => ({ default: m.DatabasePerformancePage })),
);

function LegacyDevicesRedirect() {
  return <Navigate to="/equipment/devices" replace />;
}

function LegacyDevicesNewRedirect() {
  return <Navigate to="/equipment/devices/new" replace />;
}

function LegacyDevicesMacRedirect() {
  return <Navigate to="/equipment/devices/search/mac" replace />;
}

function LegacyDeviceDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/equipment/devices" replace />;
  }
  return <Navigate to={`/equipment/devices/${id}`} replace />;
}

function LegacyDeviceEditRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/equipment/devices" replace />;
  }
  return <Navigate to={`/equipment/devices/${id}/edit`} replace />;
}

function LegacyDeviceTabRedirect() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  if (!id) {
    return <Navigate to="/equipment/devices" replace />;
  }
  if (!tab) {
    return <Navigate to={`/equipment/devices/${id}`} replace />;
  }
  return <Navigate to={`/equipment/devices/${id}/${tab}`} replace />;
}

export function AppRouter() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
      {/* Публичные маршруты */}
      <Route
        path="/login"
        element={
          <I18nNamespaces ns={['common','auth']} fallback={<PageFallback />}>
            <LoginPage />
          </I18nNamespaces>
        }
      />

      {/* Защищенные маршруты */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {/*
              Оболочка приложения, а не страница: ns здесь обслуживают AppHeader,
              боковое меню, глобально смонтированный SelfPasswordModal и
              PhoneSelectorPrompt — они живут на КАЖДОМ авторизованном роуте.

              `auth` и `devices` были пропущены: `auth` заявлен только на `/login`,
              поэтому диалог смены пароля показывал оператору сырые ключи
              (`changePassword`, `changePasswordCurrent`). Перевод при этом был на
              месте и полностью — namespace просто не загружался. `useTranslation`
              внутри компонента ns НЕ подгружает: ресурсы монтирует только
              `ensureNamespaces` из I18nNamespaces. `users` добавлен той же
              причине — PhoneSelectorPrompt (эталон UsmAsteriskHtml) читает
              `users:phoneSelector.*`.
            */}
            <I18nNamespaces ns={['menu', 'auth', 'devices', 'users', 'customers', 'common']} fallback={<PageFallback />}>
              <DashboardLayout />
            </I18nNamespaces>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route
          path="dashboard"
          element={
            <I18nNamespaces ns={['common', 'dashboard', 'cable', 'inventory', 'tasks', 'devices', 'customers']} fallback={<PageFallback />}>
              <DashboardPage />
            </I18nNamespaces>
          }
        />
        
        {/* Customers */}
        <Route
          path="customers"
          element={
            <I18nNamespaces ns={['common', 'customers', 'cable', 'inventory']} fallback={<PageFallback />}>
              <CustomersPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="customers/debtors"
          element={
            <I18nNamespaces ns={['common', 'customers']} fallback={<PageFallback />}>
              <CustomersDebtorsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="customers/activity"
          element={
            <I18nNamespaces ns={['common', 'customers']} fallback={<PageFallback />}>
              <CustomersActivityPage />
            </I18nNamespaces>
          }
        />
        {/* Гейт роута — customers.analytics.activity (growth-charts.1, 2026-09-01): парный
            бэкенд-гейт на /customers/activity/chart, эталон CustomerChartActivityHtml.php.
            Путь совпадает с уже существующим пунктом меню menu:customers_charts_activity —
            раньше вёл на migration-заглушку (migrationPlaceholderRoutes.tsx), запись оттуда
            убрана тем же коммитом. Второй, более узкий гейт (ADDRESS_ALL) бэкенд проверяет
            сам — фронт его не дублирует, 403 обрабатывается как обычная ошибка запроса. */}
        <Route
          path="customers/charts/activity"
          element={
            <ProtectedRoute requiredPermission="customers.analytics.activity">
              <I18nNamespaces ns={['common', 'customers']} fallback={<PageFallback />}>
                <CustomerActivityChartPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* Гейт роута — customers.analytics.growth (growth-charts.2, 2026-09-02): парный
            бэкенд-гейт на /customers/charts/growth, эталон CustomerChartIncreaseHtml.php:24
            (RIGHT_CUSTOMER_GRAF_INCREASE = 25). Путь совпадает с уже существующим пунктом
            меню menu:customers_charts_growth — раньше вёл на migration-заглушку
            (migrationPlaceholderRoutes.tsx), запись оттуда убрана тем же коммитом.
            Адресного гейта поверх права здесь нет и не нужно: отчёт разрезан по адресу,
            и выдача сужается адресным скоупом принципала в репозитории. */}
        <Route
          path="customers/charts/growth"
          element={
            <ProtectedRoute requiredPermission="customers.analytics.growth">
              <I18nNamespaces ns={['common', 'customers']} fallback={<PageFallback />}>
                <CustomerGrowthChartPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* Гейт роута — customers.ip_network (владелец, 2026-08-31): бэкенд теперь
            требует то же право на чтение, парity с v4 RIGHT_CUSTOMER_IP_NET, которое
            гейтит экран целиком (IpNetHtml.php). Без ProtectedRoute прямой заход по
            URL рисовал бы оболочку экрана кому угодно, а гейт в меню прятал бы
            только пункт — тот же класс, что чинили на /settings/address. */}
        <Route
          path="customers/ip-net"
          element={
            <ProtectedRoute requiredPermission="customers.ip_network">
              <I18nNamespaces ns={['common', 'ipNetwork', 'menu', 'monitoring']} fallback={<PageFallback />}>
                <IpNetworkPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/*
          «TOP по трафику» в v4 — не экран, а тот же список абонентов с фильтром
          «входящий трафик > 100 ГиБ» и сортировкой по убыванию
          (`CustomerListHtml.php:1081-1086`, `Html::location`). Пункт меню ведёт на
          список с пресетом; старый путь оставлен редиректом, чтобы закладка оператора
          не упиралась в отсутствующий маршрут.
        */}
        <Route path="customers/top100" element={<Navigate to="/customers?preset=top100" replace />} />
        {/*
          Экран «Метки» под ProtectedRoute, а не только под пунктом меню: скрытый пункт
          прячет вход, но не закрывает его — прямой заход по адресу оставался бы открыт.
          Право то же, что у ручки на сервере и у пункта меню (в v4 — право 33).
        */}
        <Route
          path="customers/tags"
          element={
            <ProtectedRoute requiredPermission="customers.mark.read">
              <I18nNamespaces ns={['common', 'customers', 'menu']} fallback={<PageFallback />}>
                <CustomersTagsPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/*
          «Потерянные» — как и «TOP по трафику», в v4 не экран, а список с фильтром
          (`CustomerListHtml.php:1088-1095`). Старый путь оставлен редиректом ради
          закладок оператора.
        */}
        <Route path="customers/lost" element={<Navigate to="/customers?preset=lost" replace />} />
        <Route
          path="customers/unknown-mac"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'customers', 'menu']} fallback={<PageFallback />}>
                <CustomersUnknownMacPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="customers/sms-list"
          element={
            <I18nNamespaces ns={['common', 'customers', 'menu']} fallback={<PageFallback />}>
              <CustomersSmsListPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="customers/:id"
          element={
            <ProtectedRoute requiredPermission="customers.view">
              <I18nNamespaces ns={['common', 'customers', 'tasks', 'cable', 'devices', 'inventory', 'support', 'menu']} fallback={<PageFallback />}>
                <CustomerDetailPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />

        {/* Tasks */}
        <Route
          path="tasks"
          element={
            <I18nNamespaces ns={['common', 'tasks', 'cable', 'inventory']} fallback={<PageFallback />}>
              <TasksPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="tasks/:id"
          element={
            <I18nNamespaces ns={['common', 'tasks', 'cable', 'inventory']} fallback={<PageFallback />}>
              <TaskDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="tasks/mobile"
          element={
            <I18nNamespaces ns={['common', 'tasks']} fallback={<PageFallback />}>
              <MobileTaskListPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="tasks/sla-dashboard"
          element={
            <I18nNamespaces ns={['common', 'tasks']} fallback={<PageFallback />}>
              <SLADashboardPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="tasks/quality-dashboard"
          element={
            <I18nNamespaces ns={['common', 'tasks']} fallback={<PageFallback />}>
              <QualityDashboardPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="tasks/webhooks"
          element={
            <I18nNamespaces ns={['common', 'tasks']} fallback={<PageFallback />}>
              <WebhooksPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="tasks/schedule"
          element={
            <I18nNamespaces ns={['common', 'tasks']} fallback={<PageFallback />}>
              <TaskSchedulesPage />
            </I18nNamespaces>
          }
        />

        {/* Messages */}
        <Route
          path="messages/inbox"
          element={
            <ProtectedRoute requiredPermission="customers.messages">
              <I18nNamespaces ns={['common', 'messages', 'menu']} fallback={<PageFallback />}>
                <InboxPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="messages/outbox"
          element={
            <ProtectedRoute requiredPermission="customers.messages">
              <I18nNamespaces ns={['common', 'messages', 'menu']} fallback={<PageFallback />}>
                <OutboxPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="messages/archive"
          element={
            <ProtectedRoute requiredPermission="customers.messages">
              <I18nNamespaces ns={['common', 'messages', 'menu']} fallback={<PageFallback />}>
                <ArchivePage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="messages/call-list"
          element={
            <ProtectedRoute requiredPermission="messages.calls.view">
              <I18nNamespaces ns={['common', 'messages', 'menu']} fallback={<PageFallback />}>
                <CallListPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        
        {/* Accounting */}
        <Route
          path="accounting/balance"
          element={
            <I18nNamespaces ns={['accounting', 'menu']} fallback={<PageFallback />}>
              <AccountingBalanceSummaryPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="accounting/balance-0-3"
          element={
            <I18nNamespaces ns={['accounting', 'menu', 'common']} fallback={<PageFallback />}>
              <AccountingBalance0_3Page />
            </I18nNamespaces>
          }
        />
        <Route
          path="accounting/suspect"
          element={
            <I18nNamespaces ns={['accounting', 'menu', 'common']} fallback={<PageFallback />}>
              <AccountingSuspectPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="accounting/hopeless"
          element={
            <I18nNamespaces ns={['accounting', 'menu', 'common']} fallback={<PageFallback />}>
              <AccountingHopelessPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="accounting/changes"
          element={
            <I18nNamespaces ns={['accounting', 'menu', 'common']} fallback={<PageFallback />}>
              <AccountingChangesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="accounting/fee"
          element={
            <I18nNamespaces ns={['accounting', 'menu', 'common']} fallback={<PageFallback />}>
              <AccountingFeePage />
            </I18nNamespaces>
          }
        />
        <Route
          path="accounting/fee-per-building"
          element={
            <I18nNamespaces ns={['accounting', 'menu', 'common']} fallback={<PageFallback />}>
              <AccountingFeePerBuildingPage />
            </I18nNamespaces>
          }
        />

        {/* Support Interface */}
        <Route
          path="support"
          element={
            <I18nNamespaces ns={['common', 'support', 'menu']} fallback={<PageFallback />}>
              <SupportPage />
            </I18nNamespaces>
          }
        />
        
        {/* Backup */}
        <Route
          path="backup"
          element={
            <I18nNamespaces ns={['common','backup']} fallback={<PageFallback />}>
              <BackupCenterPage />
            </I18nNamespaces>
          }
        />
        
        {/* Nodes */}
        <Route
          path="nodes"
          element={
            <I18nNamespaces ns={['common', 'nodes', 'menu']} fallback={<PageFallback />}>
              <NodesListPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="nodes/:id"
          element={
            // 'auth' — не украшение: карточка зовёт useAuth, а вместе с ним в дерево
            // приезжают ProtectedRoute и AuthProvider со своими t('auth:...').
            // Без объявления namespace оператор увидел бы сырой ключ (TD-I18N-1).
            // Ровно так же объявлены соседние роуты, чьи страницы зовут useAuth.
            <I18nNamespaces ns={['common', 'nodes', 'menu', 'auth']} fallback={<PageFallback />}>
              <NodeDetailPage />
            </I18nNamespaces>
          }
        />
        
        {/* Cable */}
        <Route
          path="cable/duct"
          element={
            <ProtectedRoute requiredPermission="cable_lines.view">
              <I18nNamespaces ns={['common', 'cable', 'auth']} fallback={<PageFallback />}>
                <CableDuctPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="cable/crosses"
          element={
            <I18nNamespaces ns={['common', 'cable', 'menu', 'auth']} fallback={<PageFallback />}>
              <CableCrossesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/crosses/:id"
          element={
            <I18nNamespaces ns={['common', 'cable', 'auth', 'inventory']} fallback={<PageFallback />}>
              <CrossDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/crosses/:id/trace/:side/:port"
          element={
            <I18nNamespaces ns={['common', 'cable', 'auth', 'inventory']} fallback={<PageFallback />}>
              <CrossDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/crosses/:id/commutations/table"
          element={
            <I18nNamespaces ns={['common', 'cable']} fallback={<PageFallback />}>
              <CrossCommutationTablePage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/lines"
          element={
            <I18nNamespaces ns={['common', 'cable', 'auth']} fallback={<PageFallback />}>
              <CableLinesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/lines/:id"
          element={
            <I18nNamespaces ns={['common', 'cable', 'auth', 'inventory']} fallback={<PageFallback />}>
              <CableLineDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/trunks"
          element={
            <I18nNamespaces ns={['common', 'cable', 'menu']} fallback={<PageFallback />}>
              <CableTrunksPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/trunks/:id"
          element={
            <I18nNamespaces ns={['common', 'cable', 'menu']} fallback={<PageFallback />}>
              <CableTrunkDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="transport-schedules"
          element={
            <ProtectedRoute requiredPermission="transport_schedule.view">
              <I18nNamespaces ns={['common', 'transportSchedule', 'menu']} fallback={<PageFallback />}>
                <TransportSchedulesPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="transport-schedules/new"
          element={
            <ProtectedRoute requiredPermission="transport_schedule.manage">
              <I18nNamespaces ns={['common', 'transportSchedule', 'cable', 'menu']} fallback={<PageFallback />}>
                <TransportScheduleFormPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="transport-schedules/:id"
          element={
            <ProtectedRoute requiredPermission="transport_schedule.view">
              <I18nNamespaces ns={['common', 'transportSchedule', 'cable', 'menu']} fallback={<PageFallback />}>
                <TransportScheduleDetailPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="transport-schedules/:id/edit"
          element={
            <ProtectedRoute requiredPermission="transport_schedule.view">
              <ProtectedRoute requiredPermission="transport_schedule.manage">
                <I18nNamespaces ns={['common', 'transportSchedule', 'cable', 'menu']} fallback={<PageFallback />}>
                  <TransportScheduleFormPage />
                </I18nNamespaces>
              </ProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="cable/splitters"
          element={
            <I18nNamespaces ns={['common', 'cable', 'auth']} fallback={<PageFallback />}>
              <CableSplittersPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/splitters/:id"
          element={
            <I18nNamespaces ns={['common', 'cable', 'auth']} fallback={<PageFallback />}>
              <CableSplitterDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="cable/global-scheme"
          element={
            <I18nNamespaces ns={['common', 'cable', 'menu']} fallback={<PageFallback />}>
              <CableGlobalSchemePage />
            </I18nNamespaces>
          }
        />
        <Route
          path="commutation-scheme/:nodeId"
          element={
            <I18nNamespaces ns={['common', 'cable']} fallback={<PageFallback />}>
              <CommutationSchemePage />
            </I18nNamespaces>
          }
        />
        {structuralSchemeConfig.launcherEnabled && (
          <Route
            path="cable/structural-scheme/:rootType/:rootId"
            element={
              <ProtectedRoute requiredPermission="cable_lines.view">
                <I18nNamespaces ns={['common', 'cable']} fallback={<PageFallback />}>
                  <StructuralSchemePage />
                </I18nNamespaces>
              </ProtectedRoute>
            }
          />
        )}

        {/* Monitoring */}
        <Route
          path="monitoring/services"
          element={
            <I18nNamespaces ns={['common','monitoring', 'menu']} fallback={<PageFallback />}>
              <ServicesMonitoringPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="monitoring/rabbitmq"
          element={
            <I18nNamespaces ns={['common','monitoring']} fallback={<PageFallback />}>
              <RabbitMQStatusPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="monitoring/redis"
          element={
            <I18nNamespaces ns={['common','monitoring']} fallback={<PageFallback />}>
              <RedisStatusPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="monitoring/grafana"
          element={
            <I18nNamespaces ns={['common', 'monitoring', 'menu']} fallback={<PageFallback />}>
              <GrafanaPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="monitoring/prometheus"
          element={
            <I18nNamespaces ns={['common', 'monitoring', 'menu']} fallback={<PageFallback />}>
              <PrometheusPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="monitoring/database"
          element={
            <I18nNamespaces ns={['common','database_monitoring']} fallback={<PageFallback />}>
              <DatabasePerformancePage />
            </I18nNamespaces>
          }
        />
        {/* Phase 3: SNMP Monitoring */}
        <Route
          path="monitoring/snmp"
          element={
            <I18nNamespaces ns={['common', 'snmp_monitoring', 'auth']} fallback={<PageFallback />}>
              <SNMPMonitoringPage />
            </I18nNamespaces>
          }
        />
        
        {/* Equipment - новые маршруты */}
        <Route path="equipment" element={<Navigate to="/equipment/devices" replace />} />
        <Route
          path="equipment/devices"
          element={
            <I18nNamespaces ns={['common', 'devices']} fallback={<PageFallback />}>
              <DevicesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/switches"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
              <DevicesSwitchesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/olt"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
              <DevicesOLTPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/onu"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
              <DevicesONUPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/radio"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
              <DevicesRadioPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/medical"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
              <DevicesMedicalPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/system"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
              <DevicesSystemPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/custom/:sectionId"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu', 'auth']} fallback={<PageFallback />}>
              <DevicesCustomSectionPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/custom-shared"
          element={
            <I18nNamespaces ns={['common', 'devices', 'menu', 'auth']} fallback={<PageFallback />}>
              <DevicesCustomSharedPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/cwdm"
          element={
            <ProtectedRoute requiredPermission="cable_lines.view">
              <I18nNamespaces ns={['common', 'devices', 'menu', 'cable']} fallback={<PageFallback />}>
                <DevicesCWDMPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="equipment/devices/cwdm/:id"
          element={
            <ProtectedRoute requiredPermission="cable_lines.view">
              <I18nNamespaces ns={['common', 'devices', 'menu', 'cable']} fallback={<PageFallback />}>
                <CwdmDetailPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Лог опроса» — эталон DeviceHtml::errorIfaceLog. Статический сегмент стоит
            ВЫШЕ карточки `equipment/devices/:id`; react-router ранжирует статику выше
            динамики, но порядок объявления оставлен явным, чтобы правка соседа не
            перевела экран на карточку с id «iface-errors». */}
        {/* Парковый список интерфейсов с ошибками — эталон DeviceHtml::errorIfaceList.
            Объявлен ВЫШЕ «Лога опроса»: `iface-errors/list` длиннее и специфичнее, и
            явный порядок защищает от того, чтобы правка соседа увела его на лог. */}
        <Route
          path="equipment/devices/iface-errors/list"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <IfaceErrorsParkPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="equipment/devices/iface-errors"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <IfaceErrorLogPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Прошивки коммутаторов» — эталон DeviceHtml::reportFirmware. Тот же приём со
            статическим сегментом ВЫШЕ карточки `equipment/devices/:id`. */}
        <Route
          path="equipment/devices/firmware-report"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <FirmwareReportPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Энергопотребление устройств» — эталон DeviceHtml::reportElectro.
            Тот же приём со статическим сегментом ВЫШЕ карточки `equipment/devices/:id`. */}
        <Route
          path="equipment/devices/electro-report"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <ElectroReportPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Уровни сигналов вне допустимых пределов» — эталон
            DeviceHtml::getRadioBadLevelReportHTML. */}
        <Route
          path="equipment/devices/radio-bad-level"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <RadioBadLevelPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Лог активности оборудования» по всему парку — эталон DeviceHtml::logEquip.
            НЕ вкладка карточки: та отвечает про одно устройство. */}
        <Route
          path="equipment/devices/activity-log"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <ActivityLogFleetPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Поиск колец» — эталон DeviceHtml::ringFind. Только чтение: эталон в этом
            отчёте удаляет строки пеленга, V5 в эту таблицу не пишет. */}
        <Route
          path="equipment/devices/ring-find"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <RingFindPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Незнакомые MAC-адреса на абонентских портах» — эталон
            DeviceHtml::customerPortNewMac. Статический сегмент ВЫШЕ карточки. */}
        <Route
          path="equipment/devices/port-new-mac"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <PortNewMACPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Число ONU по портам OLT» — эталон DeviceHtml::reportOltOnu.
            Статический сегмент ВЫШЕ карточки `equipment/devices/:id`. */}
        <Route
          path="equipment/devices/olt-onu"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <OLTONUPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «ONU видны на OLT, но ни на одном объекте не установлены» — эталон
            DeviceHtml::onuUnplug. Статический сегмент ВЫШЕ карточки `equipment/devices/:id`. */}
        <Route
          path="equipment/devices/onu-unplug"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <ONUUnplugPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Несколько абонентов на одном порту» — эталон DeviceHtml::doubleCustomer.
            Статический сегмент ВЫШЕ карточки `equipment/devices/:id`, как у соседей. */}
        <Route
          path="equipment/devices/double-customer"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <DoubleCustomerPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* «Пеленгатор MAC» — эталон DeviceHtml::pelengInfo. Статический сегмент ВЫШЕ
            карточки `equipment/devices/:id`, как у двух соседей. */}
        <Route
          path="equipment/devices/peleng"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <PelengPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="equipment/devices/vlan"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <DevicesVLANPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="equipment/devices/vlan/:vid"
          element={
            <ProtectedRoute requiredPermission="devices.view">
              <I18nNamespaces ns={['common', 'devices', 'menu']} fallback={<PageFallback />}>
                <DevicesVLANDetailPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="equipment/devices/new"
          element={
            <I18nNamespaces ns={['common', 'devices', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <DeviceNewPage />
            </I18nNamespaces>
          }
        />
        <Route path="equipment/devices/search/mac" element={<MACSearchPage />} />
        <Route
          path="equipment/devices/:deviceId/onu/:identifier"
          element={
            <I18nNamespaces ns={['common', 'devices', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <ONUDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/:id/edit"
          element={
            <I18nNamespaces ns={['common', 'devices', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <DeviceEditPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/:id/:tab"
          element={
            <I18nNamespaces ns={['common', 'devices', 'cable', 'auth']} fallback={<PageFallback />}>
              <DeviceDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/devices/:id"
          element={
            <I18nNamespaces ns={['common', 'devices', 'cable', 'auth']} fallback={<PageFallback />}>
              <DeviceDetailPage />
            </I18nNamespaces>
          }
        />

        {/* Legacy devices routes (redirects) */}
        <Route path="devices" element={<LegacyDevicesRedirect />} />
        <Route path="devices/new" element={<LegacyDevicesNewRedirect />} />
        <Route path="devices/search/mac" element={<LegacyDevicesMacRedirect />} />
        <Route path="devices/switches" element={<Navigate to="/equipment/devices/switches" replace />} />
        <Route path="devices/olt" element={<Navigate to="/equipment/devices/olt" replace />} />
        <Route path="devices/onu" element={<Navigate to="/equipment/devices/onu" replace />} />
        <Route path="devices/radio" element={<Navigate to="/equipment/devices/radio" replace />} />
        <Route path="devices/medical" element={<Navigate to="/equipment/devices/medical" replace />} />
        <Route path="devices/system" element={<Navigate to="/equipment/devices/system" replace />} />
        <Route path="devices/cwdm" element={<Navigate to="/equipment/devices/cwdm" replace />} />
        <Route path="devices/vlan" element={<Navigate to="/equipment/devices/vlan" replace />} />
        <Route path="devices/:id/edit" element={<LegacyDeviceEditRedirect />} />
        <Route path="devices/:id/:tab" element={<LegacyDeviceTabRedirect />} />
        <Route path="devices/:id" element={<LegacyDeviceDetailRedirect />} />

        {/* Equipment Catalog */}
        <Route
          path="equipment"
          element={<Navigate to="/equipment/models" replace />}
        />
        <Route
          path="equipment/models"
          element={
            <I18nNamespaces ns={['common', 'equipment']} fallback={<PageFallback />}>
              <EquipmentModelsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/webhooks"
          element={
            <I18nNamespaces ns={['common', 'equipment']} fallback={<PageFallback />}>
              <EquipmentWebhooksPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="equipment/unknown-fingerprints"
          element={
            <I18nNamespaces ns={['common', 'equipment']} fallback={<PageFallback />}>
              <UnknownFingerprintsPage />
            </I18nNamespaces>
          }
        />

        {/* Warehouse */}
        <Route
          path="warehouse/inventory"
          element={
            <I18nNamespaces ns={['common', 'inventory', 'cable']} fallback={<PageFallback />}>
              <InventoryListPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="warehouse/inventory/catalog"
          element={
            <I18nNamespaces ns={['common', 'inventory', 'menu', 'cable']} fallback={<PageFallback />}>
              <InventoryCatalogPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="warehouse/inventory/documents"
          element={
            <I18nNamespaces ns={['common','inventory']} fallback={<PageFallback />}>
              <InventoryPrintJobsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="warehouse/inventory/:id"
          element={
            <I18nNamespaces ns={['common', 'inventory', 'cable']} fallback={<PageFallback />}>
              <InventoryDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="inventory/operations/:id"
          element={
            <I18nNamespaces ns={['common', 'inventory', 'cable']} fallback={<PageFallback />}>
              <InventoryOperationPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="warehouse/reports"
          element={
            <I18nNamespaces ns={['common','inventory']} fallback={<PageFallback />}>
              <InventoryReportsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="warehouse/suppliers"
          element={
            <I18nNamespaces ns={['common', 'warehouse', 'inventory', 'menu', 'cable']} fallback={<PageFallback />}>
              <WarehouseSuppliersPage />
            </I18nNamespaces>
          }
        />
        
        {/* Map */}
        <Route
          path="map"
          element={
            <I18nNamespaces ns={['common', 'map']} fallback={<PageFallback />}>
              <Navigate to="/map/objects" replace />
            </I18nNamespaces>
          }
        />
        <Route
          path="map/buildings"
          element={
            <I18nNamespaces ns={['common', 'map']} fallback={<PageFallback />}>
              <MapBuildingsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="map/owners"
          element={
            <I18nNamespaces ns={['common','map']} fallback={<PageFallback />}>
              <MapOwnersPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="map/tenants"
          element={
            <I18nNamespaces ns={['common', 'map', 'menu']} fallback={<PageFallback />}>
              <MapTenantsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="map/keys"
          element={
            <I18nNamespaces ns={['common','map']} fallback={<PageFallback />}>
              <MapKeysPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="map/objects"
          element={
            <I18nNamespaces ns={['common', 'map', 'menu', 'cable', 'inventory']} fallback={<PageFallback />}>
              <MapObjectsPage />
            </I18nNamespaces>
          }
        />
        
        {/* Employees */}
        {/* Дерево подразделений — только чтение. Гейт employee.view: в эталоне
            экран закрыт легаси-правом 76 (EmployeeHtml.php:298), мост даёт ему
            employee.view (profile_rights_map.go:209). Тем же правом гейтится
            дочерний пункт «Подразделения» в обоих menu config; родительская группа
            намеренно без общего права, потому что отчёт и мотивация имеют независимую
            семантику видимости. Поэтому «видит подразделения, получает 403» не возникает. */}
        <Route
          path="employees/divisions"
          element={
            <ProtectedRoute requiredPermission="employee.view">
              <I18nNamespaces ns={['common', 'employees', 'menu']} fallback={<PageFallback />}>
                <EmployeesDivisionsPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* Отчёт НЕ обёрнут в ProtectedRoute сознательно: в v4 экран доступен
            любому оператору, а право employee.edit решает лишь, чей отчёт он
            видит (EmployeeHtml.php:2045-2047). Сужение до «себя» делает сервер. */}
        <Route
          path="employees/reports"
          element={
            <I18nNamespaces ns={['common', 'employees', 'menu', 'users']} fallback={<PageFallback />}>
              <EmployeesReportsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="employees/motivation"
          element={
            <I18nNamespaces ns={['common', 'employees', 'menu']} fallback={<PageFallback />}>
              <EmployeesMotivationPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="employees/vacation"
          element={
            <I18nNamespaces ns={['common', 'employees', 'menu', 'users', 'cable', 'inventory']} fallback={<PageFallback />}>
              <EmployeesVacationPage />
            </I18nNamespaces>
          }
        />

        {/* Transport */}
        {transportRoutes.map(({ path, requiredPermission, Page }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute requiredPermission={requiredPermission}>
                {/* Неймспейс `transport` объявлен здесь, а не только в `useTranslation`:
                    хук сам ничего не подгружает — бандлы добавляет `I18nNamespaces`
                    (`ensureNamespaces`). Без этой обёртки ключи `transport:*` не
                    резолвились бы, и оператор увидел бы сами ключи. */}
                <I18nNamespaces ns={['common', 'transport', 'menu']} fallback={<PageFallback />}>
                  <Page />
                </I18nNamespaces>
              </ProtectedRoute>
            }
          />
        ))}
        
        {/* Users */}
        <Route
          path="users"
          element={
            <I18nNamespaces ns={['common', 'users', 'cable', 'inventory']} fallback={<PageFallback />}>
              <UsersMainPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="users/:id"
          element={
            <I18nNamespaces ns={['common', 'users', 'cable', 'inventory']} fallback={<PageFallback />}>
              <UsersDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="users/:id/edit"
          element={
            <I18nNamespaces ns={['common', 'users', 'cable', 'inventory']} fallback={<PageFallback />}>
              <UsersEditPage />
            </I18nNamespaces>
          }
        />
        <Route path="users/main" element={<Navigate to="/users" replace />} />
        <Route
          path="users/profiles"
          element={
            <I18nNamespaces ns={['common', 'users', 'menu']} fallback={<PageFallback />}>
              <UsersProfilesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="users/profiles/create"
          element={
            <I18nNamespaces ns={['common','users']} fallback={<PageFallback />}>
              <ProfileCreatePage />
            </I18nNamespaces>
          }
        />
        <Route
          path="users/profiles/:id/edit"
          element={
            <I18nNamespaces ns={['common','users']} fallback={<PageFallback />}>
              <ProfileEditPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="users/messages"
          element={
            <I18nNamespaces ns={['common', 'users', 'menu']} fallback={<PageFallback />}>
              <UsersMessagesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="users/history"
          element={
            <I18nNamespaces ns={['common', 'users', 'menu', 'cable', 'inventory']} fallback={<PageFallback />}>
              <UsersHistoryPage />
            </I18nNamespaces>
          }
        />
        
        {/* Settings */}
        <Route
          path="settings"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsMainPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/basic"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth']} fallback={<PageFallback />}>
              <SettingsBasicPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/equipment"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsEquipmentPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/equipment/snmp-profiles"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth']} fallback={<PageFallback />}>
              <SettingsDeviceSnmpProfilesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/equipment/snmp-profiles/:id"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth']} fallback={<PageFallback />}>
              <SettingsDeviceSnmpProfileEditPage />
            </I18nNamespaces>
          }
        />
        <Route path="settings/equipment/satellites" element={<I18nNamespaces ns={['common', 'settings', 'auth']} fallback={<PageFallback />}><SettingsEquipmentSatellitesPage /></I18nNamespaces>} />
        <Route path="settings/equipment/satellites/:id" element={<I18nNamespaces ns={['common', 'settings', 'auth']} fallback={<PageFallback />}><SettingsEquipmentSatelliteEditPage /></I18nNamespaces>} />
        <Route
          path="settings/tasks"
          element={
            <I18nNamespaces ns={['common', 'settings', 'tasks', 'auth']} fallback={<PageFallback />}>
              <SettingsTasksPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/theme"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu']} fallback={<PageFallback />}>
              <SettingsThemePage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/modules"
          element={
            <I18nNamespaces ns={['common','modules','settings']} fallback={<PageFallback />}>
              <SettingsModulesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/plugins"
          element={
            <I18nNamespaces ns={['common','settings']} fallback={<PageFallback />}>
              {/* Гейт живёт ВНУТРИ SettingsPluginsPage (роль administrator, тот
                  же предикат, что у карточки adminOnly в SettingsMainPage).
                  Здесь ProtectedRoute намеренно не используется: он уводит
                  молчаливым Navigate на «/», а операторская приёмка требует
                  явного «Доступ запрещён», а не пропажи страницы. */}
              <SettingsPluginsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/modules/billing"
          element={
            <I18nNamespaces ns={['billing', 'settings', 'common', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsBillingModulesPage />
            </I18nNamespaces>
          }
        />
        {/* Редирект со старого пути /settings/billing на новый /settings/modules/billing */}
        <Route
          path="settings/billing"
          element={<Navigate to="/settings/modules/billing" replace />}
        />
        <Route
          path="settings/modules/schedules"
          element={
            <I18nNamespaces ns={['common', 'modules', 'settings']} fallback={<PageFallback />}>
              <ModuleSchedulesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cron"
          element={
            <I18nNamespaces ns={['common', 'modules', 'settings']} fallback={<PageFallback />}>
              <ModuleSchedulesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/modules/:moduleName"
          element={
            <I18nNamespaces ns={['common','modules','settings']} fallback={<PageFallback />}>
              <ModuleDetailPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="tariffs"
          element={
            <ProtectedRoute requiredPermission="customers.view">
              <I18nNamespaces ns={['common', 'tariffs', 'menu', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
                <TariffsPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/*
          «Трафик» под ProtectedRoute, а не только под пунктом меню: скрытый пункт
          не мешает зайти по прямому адресу, а API за customers.view отдаст 403 —
          оператор увидел бы пустой экран вместо честного отказа.
        */}
        <Route
          path="tariffs/traffic"
          element={
            <I18nNamespaces ns={['common', 'tariffs', 'menu', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <ProtectedRoute requiredPermission="customers.view">
                <TariffTrafficPage />
              </ProtectedRoute>
            </I18nNamespaces>
          }
        />
        <Route
          path="tariffs/groups"
          element={
            <ProtectedRoute requiredPermission="customers.view">
              <I18nNamespaces ns={['common', 'tariffs', 'menu', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
                <TariffGroupsPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="tariffs/groups/:id"
          element={
            <ProtectedRoute requiredPermission="customers.view">
              <I18nNamespaces ns={['common', 'tariffs', 'menu', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
                <TariffGroupDetailPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/additional-services"
          element={
            <ProtectedRoute requiredPermission="settings.services.manage">
              <I18nNamespaces ns={['common', 'settings', 'menu', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
                <SettingsAdditionalServicesPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/support-interface"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu']} fallback={<PageFallback />}>
              <SettingsSupportInterfacePage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/node-types"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsNodeTypesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/node-icons"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsNodeIconsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cable-lines"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsCableLinesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/customers"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'customers']} fallback={<PageFallback />}>
              <SettingsCustomersPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cable-lines/palettes"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsCableLinesPalettesPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cable-lines/catalog"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsCableCatalogPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cable-lines/catalog/:id"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsCableCatalogEditPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cable-lines/types/:id"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsCableLineTypeEditPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cable-lines/colors"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsCableColorsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/cable-lines/other"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsCableLinesOtherPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/asterisk"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu']} fallback={<PageFallback />}>
              <SettingsAsteriskPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/observer"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsObserverPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/poller"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth']} fallback={<PageFallback />}>
              <SettingsPollerPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/snmp-vendor-profiles"
          element={
            <I18nNamespaces ns={['common', 'devices', 'settings', 'auth']} fallback={<PageFallback />}>
              {/* ⚠️ Роль в системе называется `administrator` (backend: auth/domain/role.go:52,
                  RoleAdmin = "administrator"; RequireRole сравнивает строку буквально).
                  Строки `admin` не несёт ни один живой токен: у реального админа bugor
                  roles = ["customer_operator","administrator"], поэтому гейт `admin`
                  выбрасывал на дашборд ВСЕХ, включая администратора. */}
              <ProtectedRoute requiredRole="administrator">
                <VendorProfilesPage />
              </ProtectedRoute>
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/license"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth']} fallback={<PageFallback />}>
              <SettingsLicensePage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/ldap"
          element={
            <I18nNamespaces ns={['common', 'settings', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsLdapPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/map"
          element={
            <I18nNamespaces ns={['common', 'settings', 'map', 'menu', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsMapPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/geocoding"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsGeocodingPage />
            </I18nNamespaces>
          }
        />
        {/* Экран нужен владельцу любой адресной операции; контролы
            внутри разделены по create/edit/delete. */}
        <Route
          path="settings/address"
          element={
            <ProtectedRoute
              requiredAnyPermissions={['address.create', 'address.edit', 'address.delete']}
            >
              <I18nNamespaces ns={['common', 'settings', 'menu']} fallback={<PageFallback />}>
                <SettingsAddressPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* Гейт роута — право ЧТЕНИЯ API экрана (customers.extra.read), то же,
            что в settingsGates.ts. Без ProtectedRoute прямой заход по URL
            рисовал бы экран кому угодно, а гейт прятал бы только карточку
            хаба — ровно тот дефект, что чинили на /settings/address.
            Запись внутри экрана гейтится отдельно (customers.extra.write). */}
        <Route
          path="settings/additional-fields"
          element={
            <ProtectedRoute requiredPermission="customers.extra.read">
              <I18nNamespaces
                ns={['common', 'settings', 'menu', 'auth', 'cable', 'inventory']}
                fallback={<PageFallback />}
              >
                <SettingsAdditionalFieldsPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        {/* Редактор шаблонов печатных документов.

            ProtectedRoute здесь НАМЕРЕННО НЕ используется, в отличие от соседних экранов.
            Он отказывает молчаливым `<Navigate to="/">`, а приёмка этого слайса требует
            обратного: прямой заход без права обязан дать отказ, ОБЪЯСНЁННЫЙ СЛОВАМИ.
            Право V5-native и по умолчанию не выдано никому, поэтому молчаливый редирект
            оператор прочёл бы как поломку и пошёл заводить тикет. Гейт живёт внутри
            экрана (SettingsDocumentsPage), а настоящая защита — серверная: все ручки
            /settings/documents закрыты settings.documents.manage. */}
        <Route
          path="settings/documents"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsDocumentsPage />
            </I18nNamespaces>
          }
        />
        {/* Тот же паттерн, что settings/documents выше: ProtectedRoute НЕ используется —
            гейт живёт внутри экрана (SettingsObserverOidsPage), настоящая защита серверная
            (все ручки /settings/observer/oids закрыты settings.observer.manage). ОТДЕЛЬНЫЙ
            маршрут от settings/observer выше — та страница (лендинг, слайс observer-admin.6,
            закрыт 2026-08-27) больше не заглушка, ведёт на этот и остальные четыре подэкрана. */}
        <Route
          path="settings/observer/oids"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsObserverOidsPage />
            </I18nNamespaces>
          }
        />
        {/* Слайс observer-admin.2, тот же паттерн и тот же гейт settings.observer.manage. */}
        <Route
          path="settings/observer/profiles"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsObserverProfilesPage />
            </I18nNamespaces>
          }
        />
        {/* Слайс observer-admin.3, тот же паттерн и тот же гейт settings.observer.manage. */}
        <Route
          path="settings/observer/devices"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsObserverDevicesPage />
            </I18nNamespaces>
          }
        />
        {/* Слайс observer-admin.4, тот же паттерн и тот же гейт settings.observer.manage. */}
        <Route
          path="settings/observer/readings"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsObserverReadingsPage />
            </I18nNamespaces>
          }
        />
        {/* Слайс observer-admin.5, тот же паттерн и тот же гейт settings.observer.manage. */}
        <Route
          path="settings/observer/log"
          element={
            <I18nNamespaces ns={['common', 'settings', 'menu', 'auth']} fallback={<PageFallback />}>
              <SettingsObserverLogPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/warehouse"
          element={
            <I18nNamespaces ns={['common', 'inventory', 'menu', 'cable']} fallback={<PageFallback />}>
              <WarehouseSettingsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/additional-fields/:id"
          element={
            <ProtectedRoute requiredPermission="customers.extra.read">
              <I18nNamespaces
                ns={['common', 'settings', 'menu', 'auth', 'cable', 'inventory']}
                fallback={<PageFallback />}
              >
                <SettingsAdditionalFieldEditPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/object-marks"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsObjectMarksPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="settings/object-marks/:id"
          element={
            <I18nNamespaces ns={['common', 'settings', 'auth', 'cable', 'inventory']} fallback={<PageFallback />}>
              <SettingsObjectMarkEditPage />
            </I18nNamespaces>
          }
        />
        
        {/* Other */}
        <Route
          path="advertisement"
          element={
            <ProtectedRoute requiredPermission="advertising.view">
              <I18nNamespaces ns={['common', 'advertisement', 'menu']} fallback={<PageFallback />}>
                <AdvertisementListPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="advertisement/new"
          element={
            <ProtectedRoute requiredPermission="advertising.manage">
              <I18nNamespaces ns={['common', 'advertisement', 'menu', 'cable', 'inventory']} fallback={<PageFallback />}>
                <AdvertisementFormPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="advertisement/:id"
          element={
            <ProtectedRoute requiredPermission="advertising.view">
              <I18nNamespaces ns={['common', 'advertisement', 'menu', 'cable', 'inventory']} fallback={<PageFallback />}>
                <AdvertisementDetailPage />
              </I18nNamespaces>
            </ProtectedRoute>
          }
        />
        <Route
          path="notepad"
          element={
            <I18nNamespaces ns={['common', 'notepad', 'menu']} fallback={<PageFallback />}>
              <NotepadPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="statistics"
          element={
            <I18nNamespaces ns={['common', 'statistics', 'menu']} fallback={<PageFallback />}>
              <StatisticsPage />
            </I18nNamespaces>
          }
        />
        <Route
          path="sms/send"
          element={
            <I18nNamespaces ns={['common', 'sms', 'menu']} fallback={<PageFallback />}>
              <SmsSendPage />
            </I18nNamespaces>
          }
        />

        {/*
          Разделы, ещё не перенесённые из v4.
          Пункты меню и карточки хаба настроек на них ведут, поэтому маршрут обязан
          существовать: без него SPA-catch-all молча уводит оператора на главную
          (либо путь схлопывается в соседний `:id`-маршрут и рисует чужую страницу).
          Мигрируя раздел — заменить строку на настоящий экран, пункт меню не трогать.
        */}
        {MIGRATION_PLACEHOLDER_ROUTES.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <I18nNamespaces ns={['common', 'menu']} fallback={<PageFallback />}>
                <MigrationPlaceholder
                  titleKey={route.titleKey}
                  icon={route.icon}
                  descriptionKey={route.descriptionKey}
                  relatedNoteKey={route.relatedNoteKey}
                  related={route.related}
                />
              </I18nNamespaces>
            }
          />
        ))}
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
